import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Hero from '../Hero';
import Services from '../Services';
import References from '../References';
import Footer from '../Footer';
import LanguageSwitcher from '../LanguageSwitcher';
import PrivacySection from '../PrivacySection';
import TechnologiesSection from '../TechnologiesSection';
import ContactInfo from '../ContactInfo';
import CaseStudiesSection from '../CaseStudiesSection';
import Careers from '../Careers';
import Chatbot from '../Chatbot';

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [darkMode, setDarkMode] = useState(true);
  const [activeSection, setActiveSection] = useState('hero');
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      
      // Aktif section'ı belirle
      const sections = ['hero', 'services', 'technologies', 'references', 'careers', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      
      if (current) {
        setActiveSection(current);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const navbarVariants = {
    top: {
      backgroundColor: 'rgba(0, 0, 0, 0)',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      y: 0
    },
    scrolled: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'rgba(255, 255, 255, 0.2)',
      y: 0
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navItems = [
    { id: 'hero', label: t('nav.home') },
    { id: 'services', label: t('nav.services') },
    { id: 'technologies', label: t('nav.technologies') },
    { id: 'references', label: t('nav.references') },
    { id: 'careers', label: t('nav.careers') },
    { id: 'contact', label: t('nav.contact') }
  ];

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-black text-white' : 'bg-white text-black'}`}>
      
      <motion.header 
        className="fixed top-0 w-full z-50 py-4 px-6 backdrop-blur-md flex justify-between items-center border-b border-gray-800"
        variants={navbarVariants}
        initial="top"
        animate={scrollY > 50 ? "scrolled" : "top"}
        transition={{ duration: 0.3 }}
      >
        <motion.div 
          className="flex items-center"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          whileHover={{ scale: 1.05 }}
        >
          <img src="/ajan-logo.svg" alt="Ajan Logo" className="h-10 invert" />
          <motion.span 
            className="ml-2 text-xl font-bold"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            AJAN
          </motion.span>
        </motion.div>

        <nav className="hidden md:flex space-x-6">
          {navItems.map((item, index) => (
            <motion.button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`px-3 py-2 rounded-md transition-colors ${
                activeSection === item.id 
                  ? 'text-cyan-400 bg-cyan-400/10' 
                  : 'text-gray-300 hover:text-cyan-400'
              }`}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {item.label}
            </motion.button>
          ))}
        </nav>

        <div className="flex items-center space-x-4">
          <LanguageSwitcher />
          
          <motion.button
            onClick={() => setDarkMode(!darkMode)}
            className="p-2 rounded-full text-gray-300 hover:text-white hover:bg-gray-800"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {darkMode ? '☀️' : '🌙'}
          </motion.button>
        </div>
      </motion.header>

      <main>
        <section id="hero">
          <Hero />
        </section>
        
        <section id="services">
          <Services />
        </section>
        
        <section id="technologies">
          <TechnologiesSection />
        </section>
        
        <section id="references">
          <References />
        </section>

        <section id="careers">
          <Careers />
        </section>
        
        <CaseStudiesSection />
        <PrivacySection />
        
        <section id="contact">
          <ContactInfo />
        </section>
        
        <Footer />
      </main>

      <Chatbot />

      {/* Floating WhatsApp Button - Sol altta konumlandırıldı */}
      <motion.a
        href="https://wa.me/905553334455?text=Merhaba! Dijital çözümleriniz hakkında bilgi almak istiyorum."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 left-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg transition-colors duration-200"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 0.5 }}
      >
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
        </svg>
      </motion.a>
    </div>
  );
};

export default HomePage;